import { Trans, useTranslation } from 'next-i18next'

import { useStateData } from '@/lib/locations'
import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/university/content-template'
import useFilterSlug from '@/features/Listings/seo/search-results-content/content/use-filter-slug'

const UniversityOfTorontoScarborough = ({
  filterSlug,
  cityName,
  cityNameWithState,
  cityData,
  neighborhoodName,
  neighbourhoodSlug,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const stateData = useStateData(state)
  const { slug } = stateData
  const { hasFilter, filterName } = useFilterSlug(filterSlug)

  const links = getFilterLinks(false, citySlug, neighbourhoodSlug)

  const universityGuideLink =
    'https://liv.rent/blog/neighbourhoods/best-housing-off-campus-for-university-of-toronto-students/#Renting_near_U_of_T_Scarborough_campus'

  const neighborhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/'
    : 'https://liv.rent/blog/neighbourhoods/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.hood.university-of-toronto-scarborough:About University of Toronto Scarborough Paragraph">
          The{' '}
          <LinkText href={universityGuideLink}>University of Toronto</LinkText>,
          located in the bustling city of{' '}
          <LinkText href={`/rental-listings/city/${citySlug}`}>
            Scarborough
          </LinkText>
          , <LinkText href={`/rental-listings/${slug}`}>Ontario</LinkText> is
          renowned for its unwavering dedication to excellence in teaching,
          research, and innovation. Drawing students from diverse backgrounds,
          both domestic and international, the university offers an extensive
          range of undergraduate and graduate programs tailored to cater to
          individual student needs. An integral part of its identity lies in
          nurturing a multicultural and inclusive environment that actively
          encourages students to forge meaningful connections with the local
          community.
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an apartment or room to rent')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Renting FILTER near LOCATION in Canada Paragraph">
            Finding {{ filterNameWithFallback }} near {{ neighborhoodName }}{' '}
            requires research, communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, and signing a lease. All this can add up to a
            lengthy endeavour if you’re not familiar with the rental process in
            Canada. To help you get started, here is a list of resources with
            tips on renting in Canada.
          </Trans>
        </Paragraph>
        <LinkList>
          <LinkItem href={universityGuideLink}>
            U of T Scarborough off campus guide
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
            How does rent work in Canada (compare by province)
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
            What $1800 in rent can get you across Canada
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
            How to secure a home before arriving in Canada
          </LinkItem>
        </LinkList>
      </>
    )
  }

  const getOffCampusHousing = () => {
    const filterNameWithFallback = hasFilter
      ? filterName
      : t('seo:an affordable home')

    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.hood.university-of-toronto-scarborough:Off Campus Housing University of Toronto Scarborough Paragraph 1">
            Finding{' '}
            <LinkText href="https://liv.rent/blog/neighbourhoods/best-housing-off-campus-for-university-of-toronto-students/">
              U of T off-campus housing in Scarborough
            </LinkText>{' '}
            may be a challenge depending on where you’re looking to rent. To
            enhance the likelihood of finding {{ filterNameWithFallback }},
            acquaint yourself with various neighborhoods near the University of
            Toronto’s Scarborough campus and their average rental prices. For
            additional insights, you can explore our blog, which delves into
            details about the{' '}
            <LinkText href={neighborhoodsLink}>
              diverse neighborhoods in Canada
            </LinkText>
            .
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Off Campus Housing LOCATION Paragraph 2">
            In addition to choosing your location to rent near{' '}
            {{ neighborhoodName }}, it’s important to know what rental types are
            available (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options for renting near {{ neighborhoodName }} - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Apartments for rent near LOCATION Paragraph">
              Near {{ neighborhoodName }}, apartments are a common form of
              housing you’ll find. Average rent prices for apartments depend on
              where you’re looking.{' '}
              <LinkText href={links.apartment}>Apartments for rent</LinkText> in
              urban areas are typically more expensive due to higher demand.
              Apartments generally require much less maintenance than houses,
              making them ideal for students who don’t need as much space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.university-of-toronto-scarborough:Houses for rent near University of Toronto Scarborough Paragraph">
              There are plenty of houses{' '}
              <LinkText href={links.house}>houses for rent</LinkText> for rent
              near the University of Toronto’s Scarborough campus for those with
              a higher budget, need extra space, or who have multiple roommates.
              Both partial houses and entire houses for rent are common, as well
              as both new builds and older homes. Some areas, like{' '}
              <LinkText href="/rental-listings/city/scarborough">
                Scarborough
              </LinkText>{' '}
              and{' '}
              <LinkText href="/rental-listings/city/vancouver">
                Vancouver
              </LinkText>
              , are also known for an abundance of{' '}
              <LinkText href="https://liv.rent/blog/landlords/building-and-renting-a-laneway-house/">
                laneway homes
              </LinkText>{' '}
              and townhomes, making it easy to find a place to rent that suits
              your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent near LOCATION Paragraph">
              Townhouses are another popular form of housing near{' '}
              {{ neighborhoodName }} that combine the best qualities of both
              apartments and whole houses. Typically less expensive than houses,{' '}
              <LinkText href={links.townhouse}>townhouses for rent</LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent near LOCATION 2 Paragraph">
              For those looking to find affordable rental housing, basements for
              rent near {{ neighborhoodName }} are an excellent alternative.
              Since housing prices are so expensive, homeowners will sometimes{' '}
              <LinkText href="https://liv.rent/blog/landlords/how-to-rent-out-your-basement-suite/">
                rent out their basements
              </LinkText>{' '}
              as a way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in areas
              throughout the country.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.university-of-toronto-scarborough:Condos for rent University of Toronto Scarborough Paragraph">
              <LinkText href={links.apartment}>Condos for rent</LinkText> are
              the most common type of housing near the University of Toronto’s
              Scarborough campus. These types of rental accommodation are
              perfect for those who like to be in the centre of the action, and
              don’t need as much space. If you are looking to rent in the city
              centre though, be prepared for higher-than-average rent prices.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.university-of-toronto-scarborough:Rooms for rent near University of Toronto Scarborough Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for{' '}
              <LinkText href={links.under1000}>
                cheap rent near the University of Toronto Scarborough
              </LinkText>
              . <LinkText href={links.room}>Rooms for rent</LinkText> are a
              great way to experience wherever you’re living on a budget, and
              can offer you the opportunity to live in neighbourhoods that would
              otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals near LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Pet friendly rentals near {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals near LOCATION Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals near {{ neighborhoodName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>
                cat-friendly rental units
              </LinkText>{' '}
              are available, as well as properties where both are allowed. Try
              searching for pet-friendly units using search filters on your
              rental platform to find units that match your needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals near LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Furnished rentals near {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals near school LOCATION Paragraph">
              If you’re new to {{ cityName }} or just haven’t had a chance to
              pick up furniture of your own yet, not to worry – there are plenty
              of <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available near {{ neighborhoodName }}. Student renters seeking
              furnished units should search for these units using filters, but
              do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals near LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Short term rentals near {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.university-of-toronto-scarborough:Short term rentals near University of Toronto Scarborough Paragraph">
              Not only is the University of Toronto Scarborough an excellent
              academic institution, it also offers plenty of work opportunities.
              As a result, many people rent in Scarborough on a short-term basis
              - including staff, international students, and even parents who
              want to accompany their children for a brief period.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText>{' '}
              near most universities are exceedingly popular, particularly
              during the summer months and at the beginning of school semesters.
              The added flexibility is an appealing prospect for many renters,
              and a key factor in{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are right for you
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals near LOCATION"
              values={{
                location: neighborhoodName
              }}
              defaults="Cheap rentals near {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.hood.university-of-toronto-scarborough:Cheap rentals near University of Toronto Scarborough Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals near the University of Toronto’s Scarborough
                campus
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Scarborough, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getAmenities = () => {
    return (
      <>
        <Paragraph>
          {hasFilter ? (
            <Trans i18nKey="seo:Rental Amenities FILTER near LOCATION Paragraph 1">
              When you’re searching for {{ filterName }} near{' '}
              {{ neighborhoodName }}, make sure to look at what amenities are
              offered. For instance, check to see if the rental includes
              utilities like air conditioning, cable and internet or features
              such as a dishwasher, parking, balcony and fridge.
            </Trans>
          ) : (
            <Trans i18nKey="seo:Rental Amenities near LOCATION Paragraph 1">
              When you’re searching for{' '}
              <LinkText href={universityGuideLink}>
                off campus housing near {{ neighborhoodName }}
              </LinkText>
              , make sure to look at what amenities are offered. For instance,
              check to see if the rental includes utilities like air
              conditioning, cable and internet or features such as a dishwasher,
              parking, balcony and fridge.
            </Trans>
          )}
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Rental Amenities LOCATION Paragraph 2">
            Tip: When looking at listings on liv.rent, scroll down to the
            &quot;Amenities&quot; section to see what utilities and features are
            included with the rental.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo:Living near school in LOCATION Paragraph 1">
          <Paragraph>
            Many students often find themselves at a crossroads when it comes to
            deciding on their university and where they should live.
          </Paragraph>
          <Paragraph>
            Some students pick a university based on the city. If you’re not
            sure that {{ cityName }} is the right city for you, check out some
            of these resources comparing the different cities.
          </Paragraph>
        </Trans>
        <LinkList>
          <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
            Best Canadian cities for immigration
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
            Vancouver vs Toronto: Cost of living comparison
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
            Toronto vs Montreal: Cost of living comparison
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/renters/cost-of-living-comparison-in-canadian-cities/">
            6 major Canadian cities: Cost of living comparison
          </LinkItem>
        </LinkList>
        <Trans i18nKey="seo:Living near school in LOCATION Paragraph 2">
          <Paragraph>
            Another choice to make is deciding between living in residence or
            off-campus. For those of you who are still deciding, check out some
            of these resources.
          </Paragraph>
        </Trans>
        <LinkList>
          <LinkItem href={universityGuideLink}>
            U of T Scarborough off campus guide
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/renters/benefits-of-living-off-campus/">
            Benefits of living off campus
          </LinkItem>
          <LinkItem href="https://liv.rent/blog/renters/the-best-off-campus-housing-for-international-students-coming-to-canada-fall-2021/">
            Best off campus housing for international students coming to Canada
          </LinkItem>
        </LinkList>
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Popular Neighborhoods near school LOCATION Paragraph">
            There are{' '}
            <LinkText href={universityGuideLink}>
              many neighbourhoods near {{ neighborhoodName }}
            </LinkText>{' '}
            that you can consider if you’re looking to live off campus. For more
            information, you can check the blog to learn about the different
            neighbourhoods near {{ neighborhoodName }}.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    offCampusHousing: getOffCampusHousing(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: getAmenities(),
    living: getLiving(),
    popularHoods: getPopularHoods()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
      neighborhoodName={neighborhoodName}
      neighbourhoodSlug={neighbourhoodSlug}
    />
  )
}

export default UniversityOfTorontoScarborough
